import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../logo.png";
import sharkShadow from "../assets/shark-facing-right.svg";
import { auth, googleProvider, requestPasswordReset } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { motion, useReducedMotion } from "framer-motion";
import SideRays from "../components/SideRays";
import "./Login.css";
import { trackEvent, trackPageView } from "../statsig";
import { useLingui } from "@lingui/react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const translations = {
  en: {
    createAccount: "Create Account",
    welcomeBack: "Welcome Back",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Password",
    signUp: "Sign Up",
    logIn: "Log In",
    alreadyAccount: "Already have an account?",
    dontHaveAccount: "Don't have an account?",
    continueGoogle: "Continue with Google",
    rememberMe: "Remember Me",
    errors: {
      "auth/invalid-email": "Invalid email address.",
      "auth/user-not-found": "No account found with this email.",
      "auth/wrong-password": "Incorrect password.",
      "auth/email-already-in-use": "This email is already in use.",
      "auth/weak-password": "Password should be at least 6 characters.",
      "auth/popup-closed-by-user": "Google sign-in was cancelled.",
      "auth/admin-restricted-operation": "This account has not been enabled. Please contact the FinTitles administrator.",
      default: "Something went wrong. Please try again.",
    },
  },
  he: {
    createAccount: "צור חשבון",
    welcomeBack: "ברוך שובך",
    emailPlaceholder: "אימייל",
    passwordPlaceholder: "סיסמה",
    signUp: "הרשמה",
    logIn: "התחברות",
    alreadyAccount: "כבר יש לך חשבון?",
    dontHaveAccount: "אין לך חשבון?",
    continueGoogle: "המשך עם גוגל",
    rememberMe: "זכור אותי",
    errors: {
      "auth/invalid-email": "כתובת אימייל לא תקינה.",
      "auth/user-not-found": "לא נמצא חשבון עם האימייל הזה.",
      "auth/wrong-password": "סיסמה שגויה.",
      "auth/email-already-in-use": "האימייל הזה כבר בשימוש.",
      "auth/weak-password": "הסיסמה צריכה להכיל לפחות 6 תווים.",
      "auth/popup-closed-by-user": "ההתחברות עם גוגל בוטלה.",
      "auth/admin-restricted-operation": "החשבון הזה אינו מורשה. נא לפנות למנהל FinTitles.",
      default: "משהו השתבש. נסה שוב.",
    },
  },
};

const resetTranslations = {
  en: {
    forgotPassword: "Forgot password?",
    resetPassword: "Reset Password",
    sendResetEmail: "Send reset email",
    sending: "Sending...",
    backToLogin: "Back to login",
    emailRequired: "Please enter your email.",
    invalidEmail: "Please enter a valid email address.",
    tooManyRequests: "Too many requests. Please wait and try again.",
    genericError: "We couldn't send the reset email. Please try again.",
    success: "If an account exists for this email, a password reset link was sent.",
  },
  he: {
    forgotPassword: "שכחת את הסיסמה?",
    resetPassword: "איפוס סיסמה",
    sendResetEmail: "שליחת קישור לאיפוס",
    sending: "שולח...",
    backToLogin: "חזרה להתחברות",
    emailRequired: "נא להזין כתובת אימייל.",
    invalidEmail: "נא להזין כתובת אימייל תקינה.",
    tooManyRequests: "יותר מדי בקשות. נא להמתין ולנסות שוב.",
    genericError: "לא הצלחנו לשלוח את הודעת האיפוס. נא לנסות שוב.",
    success: "אם קיים חשבון עבור כתובת האימייל הזו, נשלח קישור לאיפוס הסיסמה.",
  },
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type MessageType = "error" | "success";

export default function Login() {
  const navigate = useNavigate();
  const postLoginDestination = "/app/projects";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<MessageType>("error");
  const [isSigningUp] = useState(false);
  const [isPasswordResetMode, setIsPasswordResetMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { i18n } = useLingui();
  const lang: "en" | "he" = i18n.locale === "he" ? "he" : "en";
  const rememberMe = true;
  const shouldReduceMotion = useReducedMotion();
  const passwordResetRequestRef = useRef(false);
  const googlePopupPendingRef = useRef(false);
  const googlePopupHasLostFocusRef = useRef(false);
  const googlePopupRecoveryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const googleSignInAttemptRef = useRef(0);
  const t = translations[lang];
  const resetText = resetTranslations[lang];
  const authTitle = isPasswordResetMode
    ? resetText.resetPassword
    : isSigningUp
      ? t.createAccount
      : t.welcomeBack;

  const getFriendlyAuthMessage = useCallback((code: string): string => {
    return (t.errors as any)[code] || t.errors.default;
  }, [t]);

  // Remove the legacy saved email so a refresh always starts with an empty field.
  useEffect(() => {
    localStorage.removeItem("rememberedEmail");
  }, []);

  useEffect(() => {
    trackPageView("login");
  }, []);

  useEffect(() => {
    const noteGooglePopupOpened = () => {
      if (googlePopupPendingRef.current) {
        googlePopupHasLostFocusRef.current = true;
      }
    };

    const recoverFromClosedGooglePopup = () => {
      if (!googlePopupPendingRef.current || !googlePopupHasLostFocusRef.current) return;

      const attempt = googleSignInAttemptRef.current;

      if (googlePopupRecoveryTimeoutRef.current) {
        clearTimeout(googlePopupRecoveryTimeoutRef.current);
      }

      // A completed OAuth callback also returns focus here. A brief grace period
      // lets Firebase finish that callback before treating the popup as cancelled.
      googlePopupRecoveryTimeoutRef.current = setTimeout(() => {
        googlePopupRecoveryTimeoutRef.current = null;
        if (
          !googlePopupPendingRef.current ||
          googleSignInAttemptRef.current !== attempt ||
          auth.currentUser
        ) return;

        googlePopupPendingRef.current = false;
        googlePopupHasLostFocusRef.current = false;
        setLoading(false);
        setMessage(getFriendlyAuthMessage("auth/popup-closed-by-user"));
        setMessageType("error");
      }, 300);
    };

    window.addEventListener("blur", noteGooglePopupOpened);
    window.addEventListener("focus", recoverFromClosedGooglePopup);
    return () => {
      window.removeEventListener("blur", noteGooglePopupOpened);
      window.removeEventListener("focus", recoverFromClosedGooglePopup);
      if (googlePopupRecoveryTimeoutRef.current) {
        clearTimeout(googlePopupRecoveryTimeoutRef.current);
      }
    };
  }, [getFriendlyAuthMessage]);

  const invalidateGoogleSignInAttempt = () => {
    googleSignInAttemptRef.current += 1;
    googlePopupPendingRef.current = false;
    googlePopupHasLostFocusRef.current = false;
    if (googlePopupRecoveryTimeoutRef.current) {
      clearTimeout(googlePopupRecoveryTimeoutRef.current);
      googlePopupRecoveryTimeoutRef.current = null;
    }
  };

  // handleAuth supports being called as form submit or direct click
  const handleAuth = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email) {
      setMessage("Please enter an email.");
      setMessageType("error");
      return;
    }
    if (!isSigningUp && !password) {
      setMessage("Please enter your password.");
      setMessageType("error");
      return;
    }

    invalidateGoogleSignInAttempt();
    setLoading(true);
    setMessage("");
    trackEvent("login_attempted", {
      method: "email_password",
      auth_mode: isSigningUp ? "sign_up" : "log_in",
      remember_me: rememberMe,
    });
    try {
      // Set persistence based on Remember Me
      await setPersistence(
        auth,
        browserLocalPersistence
      );

      if (isSigningUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }

      trackEvent("login_succeeded", {
        method: "email_password",
        auth_mode: isSigningUp ? "sign_up" : "log_in",
      });

      // Redirect into app
      navigate(postLoginDestination, { replace: true });
    } catch (err: any) {
      console.error("Auth error:", err);
      trackEvent("login_failed", {
        method: "email_password",
        auth_mode: isSigningUp ? "sign_up" : "log_in",
        error_code: err?.code ?? "unknown",
      });
      setMessage(getFriendlyAuthMessage(err.code || err.message || "default"));
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (loading) return;

    if (googlePopupRecoveryTimeoutRef.current) {
      clearTimeout(googlePopupRecoveryTimeoutRef.current);
      googlePopupRecoveryTimeoutRef.current = null;
    }
    const attempt = googleSignInAttemptRef.current + 1;
    googleSignInAttemptRef.current = attempt;
    googlePopupPendingRef.current = true;
    googlePopupHasLostFocusRef.current = false;
    setLoading(true);
    setMessage("");
    trackEvent("login_attempted", {
      method: "google",
      auth_mode: "log_in",
      remember_me: rememberMe,
    });
    try {
      await setPersistence(
        auth,
        browserLocalPersistence
      );
      await signInWithPopup(auth, googleProvider);

      if (attempt !== googleSignInAttemptRef.current) return;

      googlePopupPendingRef.current = false;
      googlePopupHasLostFocusRef.current = false;

      trackEvent("login_succeeded", {
        method: "google",
        auth_mode: "log_in",
      });

      navigate(postLoginDestination, { replace: true });
    } catch (err: any) {
      if (attempt !== googleSignInAttemptRef.current) return;

      googlePopupPendingRef.current = false;
      googlePopupHasLostFocusRef.current = false;

      console.error("Google sign-in error:", err);
      trackEvent("login_failed", {
        method: "google",
        auth_mode: "log_in",
        error_code: err?.code ?? "unknown",
      });
      setMessage(getFriendlyAuthMessage(err.code || err.message || "default"));
      setMessageType("error");
    } finally {
      if (attempt === googleSignInAttemptRef.current) {
        setLoading(false);
      }
    }
  };

  const showPasswordResetSuccess = () => {
    setMessage(resetText.success);
    setMessageType("success");
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || passwordResetRequestRef.current) return;

    const trimmedEmail = email.trim();
    setEmail(trimmedEmail);

    if (!trimmedEmail) {
      setMessage(resetText.emailRequired);
      setMessageType("error");
      return;
    }

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setMessage(resetText.invalidEmail);
      setMessageType("error");
      return;
    }

    invalidateGoogleSignInAttempt();
    passwordResetRequestRef.current = true;
    setLoading(true);
    setMessage("");

    try {
      await requestPasswordReset(trimmedEmail);
      showPasswordResetSuccess();
    } catch (err: any) {
      console.error("Password reset error:", err);

      if (err?.code === "auth/user-not-found") {
        showPasswordResetSuccess();
      } else {
        const friendlyMessage = err?.code === "auth/invalid-email"
          ? resetText.invalidEmail
          : err?.code === "auth/too-many-requests"
            ? resetText.tooManyRequests
            : resetText.genericError;
        setMessage(friendlyMessage);
        setMessageType("error");
      }
    } finally {
      passwordResetRequestRef.current = false;
      setLoading(false);
    }
  };

  const showPasswordResetMode = () => {
    if (loading) return;
    invalidateGoogleSignInAttempt();
    setIsPasswordResetMode(true);
    setMessage("");
  };

  const showLoginMode = () => {
    if (loading) return;
    invalidateGoogleSignInAttempt();
    setIsPasswordResetMode(false);
    setMessage("");
  };

  return (
    <div className="auth-page">
      <main className="auth-background">
        <img className="auth-shark-shadow" src={sharkShadow} alt="" aria-hidden="true" />
        <div className="auth-side-rays" aria-hidden="true">
          <SideRays
            speed={1.5}
            rayColor1="#EAB308"
            rayColor2="#96c8ff"
            intensity={1.6}
            spread={0.9}
            origin="top-right"
            saturation={1}
            tilt={-1}
            blend={0.72}
            falloff={2.2}
            opacity={0.72}
          />
        </div>
        <div className="auth-language">
          <LanguageSwitcher />
        </div>
        <div className="auth-wrapper" dir={lang === "he" ? "rtl" : "ltr"}>
          <motion.div
            className="auth-container"
            role="main"
            aria-labelledby="auth-heading"
            layout={!shouldReduceMotion}
            transition={{ layout: { duration: shouldReduceMotion ? 0 : 0.22, ease: "easeOut" } }}
          >
            <div className="auth-header">
              <img src={logo} alt="FinTitles" className="auth-logo" />
              <h1 id="auth-heading" className="auth-title">{authTitle}</h1>
            </div>

            <form
              onSubmit={isPasswordResetMode ? handlePasswordReset : handleAuth}
              noValidate={isPasswordResetMode}
              className="auth-form"
            >
              <label className="auth-field-label" htmlFor="email-input">{t.emailPlaceholder}</label>
              <input
                className="email-input-wrapper"
                id="email-input"
                type="email"
                placeholder=""
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                aria-label={t.emailPlaceholder}
                autoComplete="email"
                dir="ltr"
                required
              />

              {isPasswordResetMode ? (
                <>
                  <div className="button-row">
                    <button
                      className="main-button"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? resetText.sending : resetText.sendResetEmail}
                    </button>
                  </div>

                  <div className="toggle-row">
                    <button
                      className="toggle-button"
                      type="button"
                      onClick={showLoginMode}
                      disabled={loading}
                    >
                      {resetText.backToLogin}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <label className="auth-field-label" htmlFor="password-input">{t.passwordPlaceholder}</label>
                  <div className="password-input-wrapper">
                    <input
                      id="password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder=""
                      className="password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      aria-label={t.passwordPlaceholder}
                      autoComplete={isSigningUp ? "new-password" : "current-password"}
                      dir="ltr"
                    />
                    <button
                      id="password-toggle-button"
                      type="button"
                      className="password-toggle-button"
                      onClick={() => setShowPassword((v) => !v)}
                      disabled={loading}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>

                  <div className="forgot-password-row">
                    <button
                      className="toggle-button auth-link-button"
                      type="button"
                      onClick={showPasswordResetMode}
                      disabled={loading}
                    >
                      {resetText.forgotPassword}
                    </button>
                  </div>

                  <div className="button-row">
                    <button
                      className="main-button main-button--primary"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? "Loading..." : isSigningUp ? t.signUp : t.logIn}
                    </button>

                    <button
                      className="main-button main-button--google"
                      type="button"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                    >
                      {loading ? (
                        "Loading..."
                      ) : (
                        <>
                          <img
                            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                            alt="Google"
                            style={{ width: 20 }}
                            aria-hidden
                          />
                          <span>{t.continueGoogle}</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>

            {/* <div className="toggle-row">
              <span>{isSigningUp ? t.alreadyAccount : t.dontHaveAccount}</span>
              <button
                className="toggle-button"
                onClick={() => {
                  setIsSigningUp((s) => !s);
                  setMessage("");
                }}
                disabled={loading}
                aria-pressed={isSigningUp}
              >
                {isSigningUp ? t.logIn : t.signUp}
              </button>
            </div> */}

            {message && (
              <p
                className={`auth-message auth-message--${messageType}`}
                role={messageType === "error" ? "alert" : "status"}
              >
                {message}
              </p>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
